import { useMemo, useState, useEffect } from 'react';
import { getAllCategories, getCategoryWithProjects } from '../../api/category.api';
import { getProject } from '../../api/project.api';
import { updateTask, getTask, removeTaskFromWeek } from '../../api/tasks.api';
import { AuthData } from '../Authorization/AuthWrapper';
import { showError, showSuccess } from '../../services/toastServices';
import TaskDetailModal from '../../components/Task/TaskDetailModal';

const VisualizationWeekPage = (props) => {
    const { user } = AuthData();
    const [categories, setCategories] = useState([]);
    const [projects, setProjects] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [allTasks, setAllTasks] = useState([]); // Todas las tareas para mostrar en semanas
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedProject, setSelectedProject] = useState('');
    const [selectedTask, setSelectedTask] = useState('');
    const [loading, setLoading] = useState(false);
    const [dragOverWeek, setDragOverWeek] = useState(null); // Semana sobre la que se está arrastrando
    const [taskDetailModalOpen, setTaskDetailModalOpen] = useState(false);
    const [selectedTaskDetail, setSelectedTaskDetail] = useState(null);

    // Cargar categorías al montar el componente
    useEffect(() => {
        if (user.isAuthenticated) {
            setLoading(true);
            getAllCategories(
                (response) => {
                    const categoriesData = response.data?.items || [];
                    setCategories(categoriesData);
                    
                    // Extraer todas las tareas de todos los proyectos de todas las categorías
                    const tasksFromAllCategories = [];
                    categoriesData.forEach(category => {
                        if (category.projects && Array.isArray(category.projects)) {
                            category.projects.forEach(project => {
                                if (project.tasks && Array.isArray(project.tasks)) {
                                    project.tasks.forEach(task => {
                                        // Agregar información de categoría a cada tarea
                                        task.categoryId = category.id;
                                        task.categoryName = category.name;
                                        task.projectId = project.id;
                                    });
                                    tasksFromAllCategories.push(...project.tasks);
                                }
                            });
                        }
                    });
                    setAllTasks(tasksFromAllCategories);
                    setLoading(false);
                },
                (error) => {
                    console.error('Error al cargar categorías:', error);
                    showError('Error al cargar categorías');
                    setCategories([]);
                    setAllTasks([]);
                    setLoading(false);
                }
            );
        }
    }, [user]);

    // Cargar proyectos cuando se selecciona una categoría
    useEffect(() => {
        if (selectedCategory) {
            setLoading(true);
            setProjects([]);
            setTasks([]);
            setSelectedProject('');
            setSelectedTask('');
            
            getCategoryWithProjects(selectedCategory)
                .then((response) => {
                    const categoryData = response.data;
                    let projectsData = [];
                    if (Array.isArray(categoryData.projects)) {
                        projectsData = categoryData.projects;
                    } else if (Array.isArray(categoryData)) {
                        projectsData = categoryData;
                    }
                    setProjects(projectsData);
                    setLoading(false);
                })
                .catch((error) => {
                    console.error('Error al cargar proyectos:', error);
                    showError('Error al cargar proyectos');
                    setProjects([]);
                    setLoading(false);
                });
        } else {
            setProjects([]);
            setTasks([]);
            setSelectedProject('');
            setSelectedTask('');
        }
    }, [selectedCategory]);

    // Cargar tareas cuando se selecciona un proyecto
    useEffect(() => {
        if (selectedProject) {
            setLoading(true);
            setTasks([]);
            setSelectedTask('');
            
            getProject(selectedProject)
                .then((response) => {
                    const projectData = response.data;
                    const tasksData = Array.isArray(projectData.tasks) ? projectData.tasks : [];
                    setTasks(tasksData);
                    setLoading(false);
                })
                .catch((error) => {
                    console.error('Error al cargar tareas:', error);
                    setTasks([]);
                    setLoading(false);
                });
        } else {
            setTasks([]);
            setSelectedTask('');
        }
    }, [selectedProject]);
    
    // Contar tareas activas de un proyecto (estado 0 o 1)
    const getActiveTasksCount = (project) => {
        if (!project.tasks || !Array.isArray(project.tasks)) return 0;
        return project.tasks.filter(task => task.status === 0 || task.status === 1).length;
    };

    // Filtrar solo tareas activas (estado 0 o 1)
    const activeTasks = useMemo(() => {
        return tasks.filter(task => task.status === 0 || task.status === 1);
    }, [tasks]);

    // Obtener la tarea seleccionada
    const currentTask = useMemo(() => {
        return activeTasks.find(t => String(t.id) === String(selectedTask));
    }, [selectedTask, activeTasks]);

    // Generar el código de semana (formato: YYYY-WW)
    const getWeekCode = (week) => {
        const year = week.start.getFullYear();
        const weekNum = String(week.number).padStart(2, '0');
        return `${year}-${weekNum}`;
    };

    // Obtener tareas asignadas a una semana específica
    const getTasksForWeek = (weekCode) => {
        return allTasks.filter(task => 
            task.assignedToWeeks && Array.isArray(task.assignedToWeeks) && task.assignedToWeeks.includes(weekCode)
        );
    };

    // Abrir detalle de tarea
    const handleTaskClick = (taskId) => {
        // Buscar la tarea en allTasks (tiene información de categoría)
        const taskFromMemory = allTasks.find(t => String(t.id) === String(taskId));
        
        if (!taskFromMemory) {
            showError('Tarea no encontrada');
            return;
        }

        setLoading(true);
        getTask(taskId, (error) => {
            console.error('Error al cargar tarea:', error);
            showError('Error al cargar detalle de tarea');
            setLoading(false);
        })
            .then((response) => {
                // Combinar datos de la API con la información de categoría
                const taskWithCategory = {
                    ...response.data,
                    categoryId: taskFromMemory.categoryId,
                    categoryName: taskFromMemory.categoryName,
                    projectId: taskFromMemory.projectId
                };
                setSelectedTaskDetail(taskWithCategory);
                setTaskDetailModalOpen(true);
                setLoading(false);
            })
            .catch((error) => {
                console.error('Error al cargar tarea:', error);
                showError('Error al cargar detalle de tarea');
                setLoading(false);
            });
    };

    // Cerrar modal
    const handleCloseModal = () => {
        setTaskDetailModalOpen(false);
        setSelectedTaskDetail(null);
    };
    const handleDragStart = (e) => {
        if (!selectedTask) {
            e.preventDefault();
            return;
        }
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('taskId', selectedTask);
    };

    // Manejar drag over
    const handleDragOver = (e, week) => {
        if (!week.isActive && !week.isFuture) {
            return;
        }
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverWeek(week.number);
    };

    // Manejar drag leave
    const handleDragLeave = (e) => {
        setDragOverWeek(null);
    };

    // Manejar drop
    const handleDrop = (e, week) => {
        e.preventDefault();
        setDragOverWeek(null); // Limpiar el resaltado
        if (!week.isActive && !week.isFuture) {
            showError('No puedes asignar tareas a semanas anteriores');
            return;
        }
        const taskId = e.dataTransfer.getData('taskId');
        
        if (!taskId || !currentTask) {
            showError('Por favor selecciona una tarea');
            return;
        }

        const weekCode = getWeekCode(week);
        
        // Verificar si ya está asignada a esta semana
        const currentWeeks = currentTask.assignedToWeeks || [];
        if (currentWeeks.includes(weekCode)) {
            showError(`La tarea ya está asignada a la semana ${week.number}`);
            return;
        }

        const updatedAssignedWeeks = [...currentWeeks, weekCode];

        setLoading(true);
        
        // Enviar toda la tarea con el código de la semana a agregar
        const payload = {
            name: currentTask.name,
            richDescription: currentTask.richDescription,
            size: currentTask.size,
            status: currentTask.status,
            deadline: currentTask.deadline,
            assignedToWeek: weekCode
        };


        updateTask(taskId, payload,
            (response) => {
                showSuccess(`Tarea asignada a la semana ${week.number}`);
                
                // Actualizar la tarea en ambos estados
                const updatedTask = { ...currentTask, assignedToWeeks: updatedAssignedWeeks };
                setTasks(tasks.map(t => String(t.id) === String(taskId) ? updatedTask : t));
                setAllTasks(allTasks.map(t => String(t.id) === String(taskId) ? updatedTask : t));
                
                setLoading(false);
            },
            (error) => {
                console.error('Error al actualizar tarea:', error);
                showError('Error al asignar tarea a la semana');
                setLoading(false);
            }
        );
    };

    const handleRemoveTaskFromWeek = (e, taskId, weekCode) => {
        e.stopPropagation();
        const shouldRemove = window.confirm('¿Remover esta tarea de la semana?');
        if (!shouldRemove) {
            return;
        }
        setLoading(true);

        removeTaskFromWeek(
            taskId,
            { removeFromWeek: weekCode },
            (response) => {
                showSuccess('Tarea removida de la semana');

                const updateWeekRemoval = (task) => {
                    if (String(task.id) !== String(taskId)) return task;
                    const updatedWeeks = (task.assignedToWeeks || []).filter(w => w !== weekCode);
                    return { ...task, assignedToWeeks: updatedWeeks };
                };

                setTasks(tasks.map(updateWeekRemoval));
                setAllTasks(allTasks.map(updateWeekRemoval));
                setLoading(false);
            },
            (error) => {
                console.error('Error al remover tarea de la semana:', error);
                showError('Error al remover tarea de la semana');
                setLoading(false);
            }
        );
    };
    // Generar las semanas del año a partir de ahora
    const weeks = useMemo(() => {
        const today = new Date();
        const year = today.getFullYear();
        const weeks = [];
        
        // Obtener el primer día del año
        const firstDay = new Date(year, 0, 1);
        
        // Calcular la semana del primer día del año
        const dayOfWeek = firstDay.getDay();
        const startDate = new Date(firstDay);
        startDate.setDate(startDate.getDate() - dayOfWeek);
        
        // Generar semanas
        let currentDate = new Date(startDate);
        let weekNumber = 1;
        
        while (currentDate.getFullYear() <= year) {
            const weekStart = new Date(currentDate);
            const weekEnd = new Date(currentDate);
            weekEnd.setDate(weekEnd.getDate() + 6);
            
            // Solo incluir si la semana contiene días del año actual
            if (weekEnd.getFullYear() >= year && weekStart.getFullYear() <= year) {
                weeks.push({
                    number: weekNumber,
                    start: new Date(weekStart),
                    end: new Date(weekEnd),
                    isActive: weekStart <= today && today <= weekEnd,
                    isFuture: weekStart > today
                });
            }
            
            currentDate.setDate(currentDate.getDate() + 7);
            weekNumber++;
        }
        
        return weeks;
    }, []);

    const formatDate = (date) => {
        return date.toLocaleDateString('es-ES', { 
            day: '2-digit', 
            month: '2-digit'
        });
    };

    const getWeekColor = (week) => {
        if (week.isActive) {
            return 'bg-blue-500 border-blue-600';
        }
        if (week.isFuture) {
            return 'bg-gray-200 border-gray-300';
        }
        return 'bg-gray-100 border-gray-200';
    };

    const getTextColor = (week) => {
        if (week.isActive) {
            return 'text-white';
        }
        return 'text-gray-800';
    };

    const weeksByQuarter = useMemo(() => {
        const quarters = { 1: [], 2: [], 3: [], 4: [] };
        weeks.forEach((week) => {
            const month = week.start.getMonth();
            const quarter = Math.floor(month / 3) + 1;
            quarters[quarter].push(week);
        });
        return Object.entries(quarters).map(([quarter, quarterWeeks]) => ({
            quarter: `Q${quarter}`,
            weeks: quarterWeeks,
        }));
    }, [weeks]);

    return (
        <div className="mainContainer">
            <div className='flex flex-col gap-6 w-full'>
                <div className='w-full'>
                    <div className="font-bold text-2xl mb-2">Visualización de semanas</div>
                    <div className="text-gray-500 mb-6">Semanas del año {new Date().getFullYear()}</div>
                    
                    {/* Filtros - Dropdowns */}
                    <div className='grid grid-cols-2 gap-6 mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200 w-full'>
                        {/* Columna izquierda: Categoría + Proyecto */}
                        <div className='flex flex-col gap-4'>
                            <div className='flex flex-col gap-2'>
                                <label className='font-semibold text-gray-700'>Categoría</label>
                                <select
                                    value={selectedCategory}
                                    onChange={(e) => setSelectedCategory(e.target.value)}
                                    className='p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
                                    disabled={loading}
                                >
                                    <option value=''>Seleccionar categoría...</option>
                                    {categories.map((category) => (
                                        <option key={category.id} value={category.id}>
                                            {category.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className='flex flex-col gap-2'>
                                <label className='font-semibold text-gray-700'>Proyecto</label>
                                <select
                                    value={selectedProject}
                                    onChange={(e) => setSelectedProject(e.target.value)}
                                    className='p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
                                    disabled={!selectedCategory || loading}
                                >
                                    <option value=''>
                                        {!selectedCategory ? 'Selecciona una categoría primero' : 'Seleccionar proyecto...'}
                                    </option>
                                    {projects.map((project) => {
                                        const activeCount = getActiveTasksCount(project);
                                        return (
                                            <option key={project.id} value={project.id}>
                                                {project.name} ({activeCount})
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                        </div>

                        {/* Columna derecha: Tarea + tarea seleccionada */}
                        <div className='flex flex-col gap-2'>
                            <label className='font-semibold text-gray-700'>Tarea</label>
                            <div className='flex flex-col gap-2'>
                                <select
                                    value={selectedTask}
                                    onChange={(e) => setSelectedTask(e.target.value)}
                                    className='p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
                                    disabled={!selectedProject || loading}
                                >
                                    <option value=''>
                                        {!selectedProject ? 'Selecciona un proyecto primero' : 'Seleccionar tarea...'}
                                    </option>
                                    {activeTasks.map((task) => (
                                        <option key={task.id} value={task.id}>
                                            {task.name}
                                        </option>
                                    ))}
                                </select>

                                {selectedTask && currentTask && (
                                    <div className='flex flex-col gap-2 mt-2'>
                                        <span className='text-xs text-gray-500'>Arrastra esta tarea a una semana:</span>
                                        <div
                                            draggable
                                            onDragStart={handleDragStart}
                                            className='w-36 h-12 px-3 rounded-md bg-blue-500 text-white text-xs font-semibold flex items-center justify-center cursor-move hover:bg-blue-600 transition-colors shadow-sm'
                                            title='Arrastra esta tarea a una semana'
                                        >
                                            <span className='truncate'>{currentTask.name}</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {loading && (
                                <div className='text-sm text-blue-600 font-semibold'>Cargando...</div>
                            )}
                        </div>
                    </div>

                </div>
                
                {/* Semanas agrupadas por trimestres */}
                <div className='flex flex-col gap-6 w-full'>
                    {weeksByQuarter.map((group) => (
                        <div key={group.quarter} className='w-full'>
                            <div className='font-bold text-lg mb-3 text-gray-700'>{group.quarter}</div>
                            <div className='flex flex-wrap gap-4 w-full justify-start'>
                                {group.weeks.map((week) => {
                                    const weekCode = getWeekCode(week);
                                    const weekTasks = getTasksForWeek(weekCode);
                                    const isDragOver = dragOverWeek === week.number;
                                    const canDrop = week.isActive || week.isFuture;

                                    return (
                                        <div
                                            key={week.number}
                                            onDragOver={(e) => handleDragOver(e, week)}
                                            onDragLeave={handleDragLeave}
                                            onDrop={(e) => handleDrop(e, week)}
                                            className={`w-60 h-60 p-4 rounded-lg border-2 transition-all hover:shadow-lg flex flex-col ${getWeekColor(week)} ${selectedTask && canDrop ? 'cursor-move' : 'cursor-not-allowed'} ${isDragOver ? 'ring-4 ring-green-400 shadow-2xl scale-105' : ''}`}
                                        >
                                            {/* Header de la semana */}
                                            <div className='flex flex-col mb-3'>
                                                <div className={`font-bold text-xl ${getTextColor(week)}`}>
                                                    Sem {week.number}
                                                </div>
                                                <div className={`text-xs mt-1 ${getTextColor(week)}`}>
                                                    {formatDate(week.start)} - {formatDate(week.end)}
                                                </div>
                                                {week.isActive && (
                                                    <div className='mt-2 px-2 py-1 bg-blue-600 text-white rounded-full text-xs font-semibold text-center'>
                                                        Actual
                                                    </div>
                                                )}
                                            </div>

                                            {/* Tareas asignadas a esta semana */}
                                            <div className='flex-1 flex flex-col gap-2 overflow-y-auto'>
                                                {weekTasks.map((task) => (
                                                    <div
                                                        key={`${week.number}-${task.id}`}
                                                        onClick={() => handleTaskClick(task.id)}
                                                        className='group px-2 py-1 bg-white rounded text-xs font-medium text-gray-800 border border-gray-300 shadow-sm truncate cursor-pointer hover:bg-blue-50 hover:border-blue-400 transition-colors flex items-center justify-between gap-2'
                                                        title={task.name}
                                                    >
                                                        <span className='truncate'>{task.name}</span>
                                                        <button
                                                            type='button'
                                                            onClick={(e) => handleRemoveTaskFromWeek(e, task.id, weekCode)}
                                                            className='text-gray-400 hover:text-red-600 p-1 rounded focus:outline-none'
                                                            aria-label='Remover tarea de la semana'
                                                            title='Remover tarea'
                                                        >
                                                            <svg className='w-3.5 h-3.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                                                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7h6m-7 0V5a1 1 0 011-1h4a1 1 0 011 1v2' />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                ))}
                                                {weekTasks.length === 0 && (
                                                    <div className={`text-xs italic ${getTextColor(week)} opacity-50`}>
                                                        Sin tareas
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Modal de detalle de tarea */}
            <TaskDetailModal
                task={selectedTaskDetail}
                isOpen={taskDetailModalOpen}
                onClose={handleCloseModal}
            />
        </div>
    );
}

export default VisualizationWeekPage;